import type { ScreenShape } from "./ScreenShape";
import type { ViewportSnapshot, ViewportStore } from "./ViewportStore";

/** A rectangle in design units with the origin at the viewport centre, y-down (the design-space root's frame). */
export interface DesignRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export function rectWidth(rect: DesignRect): number {
  return rect.right - rect.left;
}

export function rectHeight(rect: DesignRect): number {
  return rect.bottom - rect.top;
}

/** The whole viewport as a rectangle. */
export function fullViewportRect(snapshot: ViewportSnapshot): DesignRect {
  return { left: -snapshot.design.width / 2, top: -snapshot.design.height / 2, right: snapshot.design.width / 2, bottom: snapshot.design.height / 2 };
}

/** The rule a content area follows: from the viewport, the rectangle left free of chrome. */
export type ContentAreaRule = (snapshot: ViewportSnapshot) => DesignRect;

type Listener = (area: DesignRect) => void;

/**
 * The rectangle a game's presentation fits into: the viewport minus whatever chrome the rule reserves
 * (a shell decides that from its bars; presentation only knows the rectangle). Derived from the
 * viewport store: it notifies when the rectangle changes, and when the shape changes with the
 * rectangle unchanged — desktop and portrait share one, and a consumer fits it per shape
 * (finding 8, 2026-09-15).
 */
export class ContentAreaStore {
  private area: DesignRect;
  private shape: ScreenShape;
  private readonly listeners = new Set<Listener>();

  public constructor(input: { viewport: ViewportStore; rule: ContentAreaRule }) {
    const first = input.viewport.getSnapshot();
    this.area = input.rule(first);
    this.shape = first.shape;
    input.viewport.subscribe((snapshot) => {
      const next = input.rule(snapshot);
      const prev = this.area;
      const sameRect = prev.left === next.left && prev.top === next.top && prev.right === next.right && prev.bottom === next.bottom;
      if (sameRect && snapshot.shape === this.shape) return;
      this.shape = snapshot.shape;
      this.area = next;
      for (const listener of [...this.listeners]) listener(next);
    });
  }

  public getSnapshot(): DesignRect {
    return this.area;
  }

  /** Calls the listener with the current area at once, then on every change. */
  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.area);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
